import { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import Image from 'next/legacy/image'
import Wrapper from 'common/wrapper'
import { Typography } from 'common/typography'
import dynamic from 'next/dynamic'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Play } from 'components/icons'
import { TransparentButton as TB } from 'common/button'
import FigmaVideo from 'public/images/projects/figma-video.png'

const TransparentButton = styled(TB)`
  font-family: var(--primaryFont);
  font-size: 16px;
  line-height: 24px;
  color: var(--baseNeutral);
`


const VideoCardLazy = dynamic(
  () => import('../course/video-card-lazy'),
  {
    loading: () => (
      <div className="projects-video-thumb">
        <Image
          width="796"
          height="444"
          src={FigmaVideo}
          alt="Thumbail de un video de que es proyectos"
          placeholder="blur"
        />
        <div className="video-loading">
          <CircularProgress
            variant="indeterminate"
            size={48}
            thickness={6}
            className="icon"
          />
        </div>
      </div>
    ),
  },
)

const FigmaForFrontendStyled = styled.section`
  position: relative;
  margin-block-end: 64px;
  &::before {
    z-index: -1;
    position: absolute;
    right: 0;
    left: 0;
    display: block;
    top: -90px;
    transform: rotate(180deg);
    bottom: 0px;
    content: "";
  }
  .figma-explanation {
    background: var(--baseNeutral);
    color: var(--basePrimary);
    font-weight: 700;
    border-radius: 0 0 8px 8px;
    height: 48px;
    box-sizing: border-box;
    font-size: 16px;
    padding: 12px;
    line-height: 24px;
  }
  .projects-video-thumb {
    position: relative;
    cursor: pointer;
    img {
      width: 100%;
      height: auto;
      vertical-align: middle;
    }
    svg {
      right: 0px;
      left: 0px;
      bottom: 0px;
      top: 0px;
      margin: auto;
      width: 40px;
      height: 40px;
    }
  }
  .figma-video {
    bottom: 0;
    position: relative;
    border-radius: 8px;
    overflow: hidden;
  }
  h2 {
    margin-bottom: 24px;
  }
  .time-stamp {
    margin: 24px 0;
    cursor: pointer;
  }
  .time-label {
    font-weight: 700;
    margin-right: 5px;
    color: var(--turquoise);
  }
  @media(min-width: 768px) {
    margin-block-end: 128px;
    .time-stamp:first-of-type {
      margin: 0;
    }
    &::before {
      display: none;
    }
    .figma-video {
      margin-bottom: 0px;
    }
  }
  @media(min-width: 1024px) {
    .figma-content {
      display: grid;
      grid-template-columns: 380px 1fr;
      column-gap: 24px;
    }
    .figma-video {
      grid-area: 1 / 2 / 2 / 3;
    }
  }
  @media(min-width: 1024px) {
    .figma-content {
      padding: 24px;
    }
  }
`

const items = [
  {
    time: 0,
    vimeoTime: '0s',
    timeLabel: '0:00',
    title: '¿Qué es Figma?',
  },
  {
    time: 95,
    vimeoTime: '1m35s',
    timeLabel: '1:35',
    title: 'Crear una cuenta en figma',
  },
  {
    time: 115,
    vimeoTime: '1m55s',
    timeLabel: '1:55',
    title: 'Descargar proyecto',
  },
  {
    time: 156,
    vimeoTime: '2m36s',
    timeLabel: '2:36',
    title: 'Importar proyecto',
  },
  {
    time: 193,
    vimeoTime: '3m13s',
    timeLabel: '3:13',
    title: 'Recorriendo el proyecto',
  },
  {
    time: 230,
    vimeoTime: '3m50s',
    timeLabel: '3:50',
    title: 'Inspeccionando el proyecto',
  },
]

function FigmaForFrontends() {
  const [playing, setPlaying] = useState(false)
  const [startTime, setStartTime] = useState(null)
  const [player, setPlayer] = useState(null)
  const iframe = useRef(null)

  const handleCreatePlayer = async () => {
    const module = await import('@vimeo/player')
    const Player = module.default
    const vimeo = new Player(iframe.current)
    setPlayer(vimeo)
  }

  useEffect(() => {
    if (playing && iframe.current) {
      handleCreatePlayer()
    }
  }, [playing])

  const handleChangeTime = (time, startUrl) => {
    if (!playing) {
      setPlaying(true)
      setStartTime(startUrl)
    } else {
      player.setCurrentTime(time)
    }
  }

  return (
    <FigmaForFrontendStyled role="region" aria-labelledby="figma">
      <Wrapper maxWidth="1246">
        <div className="figma-container">
          <Typography color="var(--baseNeutral)" small="headline5" large="headline4" as="h2" id="figma">
            Figma para Frontends
          </Typography>
          <div className="figma-content">
            <div className="figma-video">
              {!playing
                ? (
                  <div className="projects-video-thumb" onClick={() => setPlaying(true)} role="button">
                    <Image
                      width={796}
                      height={444}
                      src={FigmaVideo}
                      alt="Thumbail de un video de que es proyectos"
                      placeholder="blur"
                    />
                    <Play position="absolute" width="60" height="60" color="#FFFFFF" />
                  </div>)
                : (
                  <VideoCardLazy
                    videoUrl="https://player.vimeo.com/video/529574735"
                    vimeoOptions={startTime ? `#t=${startTime}` : ''}
                    element={iframe}
                  />
                )
              }
              <div className="figma-explanation">
                Te explico diseños profesionales en Figma.
              </div>
            </div>
            <ul>
              {items.map(({
                time, vimeoTime, timeLabel, title,
              }) => (
                <li className="time-stamp" key={time}>
                  <TransparentButton onClick={() => handleChangeTime(time, vimeoTime)}>
                    <span className="time-label">
                      {timeLabel}
                    </span>
                    {title}
                  </TransparentButton>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Wrapper>
    </FigmaForFrontendStyled>
  )
}

export default FigmaForFrontends
